import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { PageRequest } from "./types";

type Query = Record<string, string | number | boolean | null | undefined>;

export interface ExpenseFilters {
  q?: string | undefined;
  vendorId?: string | undefined;
  kind?: "expense" | "bill" | undefined;
  status?: "unpaid" | "paid" | "void" | undefined;
  departmentId?: string | undefined;
  categoryId?: string | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

export interface JournalFilters {
  dateFrom: string;
  dateTo?: string | undefined;
  kind?: string | undefined;
  departmentId?: string | undefined;
  sourceModule?: string | undefined;
  postedBy?: string | undefined;
  q?: string | undefined;
}

/** Expenses, vendors, other income and the reports. */
export function expensesEndpoints({ http, get, post, patch, paged }: Helpers) {
  const download = (path: string, query: Query) =>
    http.request<{ blob: Blob; filename: string | null }>("GET", path, { query, responseType: "blob" });

  return {
    // Vendors
    listVendors: (page: PageRequest & { q?: string | undefined; active?: boolean | undefined } = {}) =>
      get<Out<"billing_exp_list_vendors">>("/billing/vendors", paged(page, { q: page.q, active: page.active })),
    getVendor: (id: string, signal?: AbortSignal) => get<Out<"billing_exp_get_vendor">>(`/billing/vendors/${id}`, { signal }),
    createVendor: (input: In<"billing_exp_create_vendor">) => post<Out<"billing_exp_create_vendor">>("/billing/vendors", input),
    updateVendor: (id: string, input: In<"billing_exp_update_vendor">) => patch<Out<"billing_exp_update_vendor">>(`/billing/vendors/${id}`, input),

    // Expense categories (changing them needs `billing.settings`)
    listExpenseCategories: (page: PageRequest & { q?: string | undefined; active?: boolean | undefined } = {}) =>
      get<Out<"billing_exp_list_categories">>("/billing/expense-categories", paged(page, { q: page.q, active: page.active })),
    createExpenseCategory: (input: In<"billing_exp_create_category">) =>
      post<Out<"billing_exp_create_category">>("/billing/expense-categories", input),
    updateExpenseCategory: (id: string, input: In<"billing_exp_update_category">) =>
      patch<Out<"billing_exp_update_category">>(`/billing/expense-categories/${id}`, input),

    // Expenses and vendor bills
    listExpenses: (page: PageRequest & ExpenseFilters = {}) =>
      get<Out<"billing_exp_list_expenses">>(
        "/billing/expenses",
        paged(page, {
          q: page.q,
          vendor_id: page.vendorId,
          kind: page.kind,
          status: page.status,
          department_id: page.departmentId,
          category_id: page.categoryId,
          date_from: page.dateFrom,
          date_to: page.dateTo,
        }),
      ),
    getExpense: (id: string, signal?: AbortSignal) => get<Out<"billing_exp_get_expense">>(`/billing/expenses/${id}`, { signal }),
    createExpense: (input: In<"billing_exp_create_expense">) => post<Out<"billing_exp_create_expense">>("/billing/expenses", input),
    updateExpense: (id: string, input: In<"billing_exp_update_expense">) => patch<Out<"billing_exp_update_expense">>(`/billing/expenses/${id}`, input),
    /** 409 `already_paid` / `expense_void`; 422 `card_number_refused` when the reference looks like a card number. */
    payExpense: (id: string, input: In<"billing_exp_pay_expense">) => post<Out<"billing_exp_pay_expense">>(`/billing/expenses/${id}/pay`, input),
    unpayExpense: (id: string, input: In<"billing_exp_unpay_expense">) => post<Out<"billing_exp_unpay_expense">>(`/billing/expenses/${id}/unpay`, input),
    voidExpense: (id: string, input: In<"billing_exp_void_expense">) => post<Out<"billing_exp_void_expense">>(`/billing/expenses/${id}/void`, input),

    // Other income
    listOtherIncome: (
      page: PageRequest & { q?: string | undefined; departmentId?: string | undefined; accountId?: string | undefined; status?: "active" | "void" | undefined; dateFrom?: string | undefined; dateTo?: string | undefined } = {},
    ) =>
      get<Out<"billing_exp_list_other_income">>(
        "/billing/other-income",
        paged(page, { q: page.q, department_id: page.departmentId, account_id: page.accountId, status: page.status, date_from: page.dateFrom, date_to: page.dateTo }),
      ),
    createOtherIncome: (input: In<"billing_exp_create_other_income">) => post<Out<"billing_exp_create_other_income">>("/billing/other-income", input),
    updateOtherIncome: (id: string, input: In<"billing_exp_update_other_income">) =>
      patch<Out<"billing_exp_update_other_income">>(`/billing/other-income/${id}`, input),
    voidOtherIncome: (id: string, input: In<"billing_exp_void_other_income">) =>
      post<Out<"billing_exp_void_other_income">>(`/billing/other-income/${id}/void`, input),

    // Lookups the screens need (these routes need `billing.read`)
    listExpenseAccounts: (page: PageRequest & { q?: string | undefined; type?: string | undefined; active?: boolean | undefined } = {}) =>
      get<Out<"billing_list_accounts">>("/billing/accounts", paged(page, { q: page.q, type: page.type, active: page.active })),
    listExpenseDepartments: (signal?: AbortSignal) => get<Out<"billing_list_departments">>("/billing/departments", { signal }),
    /** Households with balances, searched by name or account number (`billing.read`). */
    searchBillingHouseholds: (page: PageRequest & { q?: string | undefined } = {}) =>
      get<Out<"billing_list_accounts_with_balances">>("/billing/households", paged(page, { q: page.q })),

    // Reports (`billing.reports`)
    departmentPnl: (params: { dateFrom: string; dateTo: string; detail?: boolean }, signal?: AbortSignal) =>
      get<Out<"billing_exp_department_pnl">>("/billing/reports/department-pnl", {
        query: { date_from: params.dateFrom, date_to: params.dateTo, detail: params.detail ?? false },
        signal,
      }),
    spendingReport: (params: { dateFrom: string; dateTo: string; householdId?: string; memberId?: string; groupBy: "department" | "household" | "member" | "month"; limit?: number }, signal?: AbortSignal) =>
      get<Out<"billing_spending_report">>("/billing/reports/spending", {
        query: { date_from: params.dateFrom, date_to: params.dateTo, household_id: params.householdId, member_id: params.memberId, group_by: params.groupBy, limit: params.limit },
        signal,
      }),
    arAging: (page: PageRequest & { asOf?: string | undefined; q?: string | undefined; minBalanceMinor?: number | undefined; overdueOnly?: boolean | undefined } = {}) =>
      get<Out<"billing_ar_aging">>(
        "/billing/reports/ar-aging",
        paged(page, { as_of: page.asOf, q: page.q, min_balance_minor: page.minBalanceMinor, overdue_only: page.overdueOnly }),
      ),
    arAgingTotals: (asOf?: string, signal?: AbortSignal) =>
      get<Out<"billing_ar_aging_totals">>("/billing/reports/ar-aging/totals", { query: { as_of: asOf }, signal }),
    /** Totals come with the first page only. */
    revenueReport: (page: PageRequest & { dateFrom: string; dateTo: string; groupBy: "department" | "item"; departmentId?: string | undefined }) =>
      get<Out<"billing_revenue_report">>(
        "/billing/reports/revenue",
        paged(page, { date_from: page.dateFrom, date_to: page.dateTo, group_by: page.groupBy, department_id: page.departmentId }),
      ),
    /** Totals by kind come with the first page only. */
    journal: (page: PageRequest & JournalFilters) =>
      get<Out<"billing_journal">>(
        "/billing/reports/journal",
        paged(page, {
          date_from: page.dateFrom,
          date_to: page.dateTo,
          kind: page.kind,
          department_id: page.departmentId,
          source_module: page.sourceModule,
          posted_by: page.postedBy,
          q: page.q,
        }),
      ),
    /** A journal row carries no household id; the transaction does (`billing.read`). */
    getTransaction: (id: string, signal?: AbortSignal) => get<Out<"billing_transaction_detail">>(`/billing/transactions/${id}`, { signal }),
    reconciliation: (signal?: AbortSignal) => get<Out<"billing_reconciliation">>("/billing/reports/reconciliation", { signal }),
    /** One row per ledger line, for the accountant. */
    downloadPostingsCsv: (dateFrom: string, dateTo: string) => download("/billing/exports/postings.csv", { date_from: dateFrom, date_to: dateTo }),
    downloadExpensesCsv: (dateFrom: string, dateTo: string) => download("/billing/exports/expenses.csv", { date_from: dateFrom, date_to: dateTo }),
  };
}
